import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { type Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
	type PartCodingCodeBatchRequest,
	type PartCodingCustomerOption,
	type PartCodingCodeBatchResponse,
	type PartCodingConfirmRequest,
	type PartCodingConfirmResponse,
	type PartCodingDecisionRequest,
	type PartCodingDecisionResponse,
	type PartCodingMatchRequest,
	type PartCodingMatchResponse,
	type PartCodingParseRequest,
	type PartCodingParseResponse,
	type PartCodingPhaseTiming,
	type PartCodingSearchRequest,
	type PartCodingSearchResponse
} from '../models/part-coding';
import type {
	PartCodingQueueLine,
	PartCodingRow
} from '../models/part-coding.model';

@Injectable({
	providedIn: 'root'
})
export class PartCodingHttpService {
	private readonly http = inject(HttpClient);
	private readonly auth = inject(AuthService);
	private readonly url = `${environment.portalApi.uri}/part-coding`;

	public customers(): Observable<PartCodingCustomerOption[]> {
		return this.http.get<PartCodingCustomerOption[]>(
			`${this.url}/customers`
		);
	}

	public parse(
		request: PartCodingParseRequest
	): Observable<PartCodingParseResponse> {
		return this.http.post<PartCodingParseResponse>(
			`${this.url}/parse`,
			request
		);
	}

	public match(
		request: PartCodingMatchRequest
	): Observable<PartCodingMatchResponse> {
		return this.http.post<PartCodingMatchResponse>(
			`${this.url}/match`,
			request
		);
	}

	public async codeBatchLive(
		request: PartCodingCodeBatchRequest,
		onPhase: (phase: PartCodingPhaseTiming) => void
	): Promise<PartCodingCodeBatchResponse> {
		const token = await firstValueFrom(this.auth.generateApiToken());
		const response = await fetch(`${this.url}/batches`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${token.accessToken}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(request)
		});
		if (!response.ok) {
			const message = (await response.text()).trim();
			throw new Error(message || 'Coding failed.');
		}
		if (!response.body) {
			throw new Error('Coding failed.');
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';
		let result: PartCodingCodeBatchResponse | null = null;
		let chunk = await reader.read();
		while (!chunk.done) {
			buffer += decoder.decode(chunk.value, { stream: true });
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';
			for (const line of lines) {
				if (!line.trim()) continue;
				const message = JSON.parse(line) as {
					phase?: string;
					milliseconds?: number;
					result?: PartCodingCodeBatchResponse;
				};
				if (message.phase && message.milliseconds !== undefined) {
					onPhase({
						name: message.phase,
						milliseconds: message.milliseconds
					});
				}
				if (message.result) result = message.result;
			}
			chunk = await reader.read();
		}
		if (!result) throw new Error('Coding failed.');
		return result;
	}

	public confirmMatch(
		request: PartCodingConfirmRequest
	): Observable<PartCodingConfirmResponse> {
		return this.http.post<PartCodingConfirmResponse>(
			`${this.url}/confirmations`,
			request
		);
	}

	public queue(): Observable<PartCodingQueueLine[]> {
		return this.http.get<PartCodingQueueLine[]>(`${this.url}/queue`);
	}

	public exportBatch(
		batchId: number,
		rows: readonly PartCodingRow[]
	): Observable<{ exportedCount: number }> {
		return this.http.post<{ exportedCount: number }>(
			`${this.url}/batches/${batchId}/export`,
			{
				lines: rows.map((row) => ({
					codingLineId: row.codingLineId,
					status: row.status,
					partCode: row.partCode,
					rkDescription: row.rkDescription,
					reason: row.reason,
					quantityAvailable: row.inventory,
					isMarketBasket: row.isMarketBasket
				}))
			}
		);
	}

	public recordDecision(
		request: PartCodingDecisionRequest
	): Observable<PartCodingDecisionResponse> {
		return this.http.post<PartCodingDecisionResponse>(
			`${this.url}/decisions`,
			request
		);
	}

	public search(
		request: PartCodingSearchRequest
	): Observable<PartCodingSearchResponse> {
		return this.http.post<PartCodingSearchResponse>(
			`${this.url}/search`,
			request
		);
	}
}
